import { resolveApiBaseUrl } from '@tsgecart/config';
import type { ApiResponse, ApiErrorCode } from '@tsgecart/shared';
import { webEnv } from '../../../lib/env.js';

/** Exported so the rare non-JSON admin call (e.g. the CSV export download, which returns raw `text/csv` and can't go through `adminApiFetch`'s JSON-envelope handling) can still target the same API origin instead of hardcoding or re-resolving it. */
export const apiBaseUrl = resolveApiBaseUrl({
  environment: webEnv.VITE_APP_ENV,
  configuredUrl: webEnv.VITE_API_BASE_URL,
});

export type AdminApiErrorCode = ApiErrorCode | 'NETWORK_ERROR' | 'ABORTED' | 'PARSE_ERROR';

/**
 * Carries a stable error code/HTTP status so callers can branch (e.g.
 * AUTHENTICATION_ERROR -> prompt re-sign-in, RATE_LIMITED -> show a retry
 * countdown) without string-matching a message -- same shape as
 * apps/web/src/lib/auth/api.ts's AuthMeError, extended with the
 * network/abort/parse cases an admin dashboard's longer-lived queries can
 * actually hit that a single auth check does not.
 */
export class AdminApiError extends Error {
  constructor(
    public readonly code: AdminApiErrorCode,
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'AdminApiError';
  }
}

/**
 * Never surfaces a raw backend `error.message` for a 500 -- the Worker's
 * own error handler already never leaks stack traces or internals (see
 * docs/security-model.md), but this is a second, independent layer: even a
 * legitimate-but-unexpected 500 message is replaced with a generic string
 * here, so no future backend change can accidentally start leaking
 * implementation detail through this specific screen.
 */
function safeErrorMessage(code: ApiErrorCode, message: string): string {
  if (code === 'INTERNAL_SERVER_ERROR') {
    return 'Something went wrong loading this data. Please try again.';
  }
  return message;
}

export interface AdminApiFetchOptions {
  token: string;
  signal?: AbortSignal;
  /** Defaults to GET. Every mutation (POST/PATCH/DELETE) still goes through this same one function -- see catalog-api.ts, inventory-api.ts, media-api.ts. */
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  /** JSON-serialized automatically; never send a pre-stringified body. */
  body?: unknown;
}

/**
 * The single fetch entry point every admin feature query/mutation goes
 * through -- chart/card/widget/form components never call `fetch`
 * directly (Part 4/Part 16). The Clerk token is passed in by the caller
 * (obtained via `useAuth().getToken()` at query-time, never cached in
 * module state or localStorage) on every call, so a signed-out visitor
 * or an expired session can never reuse a stale credential.
 */
export async function adminApiFetch<T>(path: string, options: AdminApiFetchOptions): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}${path}`, {
      method: options.method ?? 'GET',
      headers: {
        Authorization: `Bearer ${options.token}`,
        ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
    });
  } catch (error) {
    if (options.signal?.aborted || (error instanceof DOMException && error.name === 'AbortError')) {
      throw new AdminApiError('ABORTED', 'Request was cancelled.', 0);
    }
    throw new AdminApiError('NETWORK_ERROR', 'Could not reach the server. Check your connection.', 0);
  }

  let body: ApiResponse<T>;
  try {
    body = (await response.json()) as ApiResponse<T>;
  } catch {
    throw new AdminApiError('PARSE_ERROR', 'Received an unexpected response from the server.', response.status);
  }

  if (!body.success) {
    throw new AdminApiError(
      body.error.code,
      safeErrorMessage(body.error.code, body.error.message),
      response.status,
    );
  }

  return body.data;
}

export interface AdminApiUploadOptions {
  token: string;
  signal?: AbortSignal;
  file: File;
  /** The multipart field name the Worker route expects -- always "file" for the current upload endpoint, kept as a parameter rather than hardcoded so a future second upload field doesn't need a second copy of this function. */
  fieldName?: string;
}

/**
 * A second, narrow entry point alongside `adminApiFetch` for the one kind
 * of request that function cannot express: a binary file body. Deliberately
 * NOT folded into `adminApiFetch`'s `body` option -- that option always
 * JSON-serializes and always sets `Content-Type: application/json`, which
 * would corrupt a file upload; letting the browser set its own
 * multipart boundary header (by never setting Content-Type manually here)
 * is required for `FormData` uploads to work at all. Otherwise mirrors
 * `adminApiFetch` exactly: same envelope parsing, same error mapping, same
 * per-call bearer token (never cached).
 */
export async function adminApiUpload<T>(path: string, options: AdminApiUploadOptions): Promise<T> {
  const formData = new FormData();
  formData.append(options.fieldName ?? 'file', options.file);

  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}${path}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${options.token}` },
      body: formData,
      signal: options.signal,
    });
  } catch (error) {
    if (options.signal?.aborted || (error instanceof DOMException && error.name === 'AbortError')) {
      throw new AdminApiError('ABORTED', 'Request was cancelled.', 0);
    }
    throw new AdminApiError('NETWORK_ERROR', 'Could not reach the server. Check your connection.', 0);
  }

  let body: ApiResponse<T>;
  try {
    body = (await response.json()) as ApiResponse<T>;
  } catch {
    throw new AdminApiError('PARSE_ERROR', 'Received an unexpected response from the server.', response.status);
  }

  if (!body.success) {
    throw new AdminApiError(
      body.error.code,
      safeErrorMessage(body.error.code, body.error.message),
      response.status,
    );
  }

  return body.data;
}
